// ═══════════════════════════════════════════════════════════════════════════
// AI Command Schemas for ENTITY-CREATION operations (CC-2c sub-arm 1)
// ═══════════════════════════════════════════════════════════════════════════
//
// Zod schemas describing the JSON the AI produces when it wants to CREATE one
// of the complex (non-primitive) entities that are BOTH `aiCallable: true` in
// the command-registry AND entity-creation per ТЗ §10.1:
//
//   CREATE_TEXT      → TEXT      → geometry/create-text
//   CREATE_MTEXT     → MTEXT     → geometry/create-mtext
//   CREATE_HATCH     → HATCH     → geometry/create-hatch
//   CREATE_DIMENSION → DIMENSION → geometry/create-dimension  (covers the whole
//                                  DIM family via `dimensionType`; only the
//                                  generic DIMENSION descriptor is aiCallable)
//   CREATE_MLEADER   → MLEADER   → geometry/create-multileader
//   CREATE_REVCLOUD  → REVCLOUD  → geometry/create-revcloud
//
// EXPLICITLY OUT OF THIS SUB-ARM (see CC-2c return / plan §3 CC-2c):
//   • base primitives (LINE/CIRCLE/ARC/PLINE) — sub-arm 4
//   • draw-modify (ARRAYRECT/ARRAYEDIT/TRIM/EXTEND/OFFSET) — sub-arm 2
//   • view/space (MVIEW/MSPACE/PSPACE/LAYOUT) — sub-arm 3
//   • WIPEOUT — has a create-wipeout contract but registry WIPEOUT.aiCallable
//     === false (not in ТЗ §10.1 AI-callable) → would fail the CC-2a guard
//   • GRADIENT / TABLE / TOLERANCE — no dedicated registry descriptor or
//     contract command exists (GRADIENT is a HATCH pattern variant)
//
// Like the block schemas, every schema is a PLAIN DATA CONTRACT — it does NOT
// mutate anything. Translation to internal `EditorCommand`s happens in
// `entityDispatcher.ts`; the reducer pipeline (the same path UI dispatches
// through) is the only place that ever writes to the document. This enforces
// the CLAUDE.md rule that AI never mutates the document directly.
//
// Design notes (mirrors blockSchemas.ts):
//   - Every action carries a literal `action: "..."` discriminator so the
//     top-level `entityAiCommandSchema` can be a Zod discriminated union.
//   - Coordinate objects reuse the shared `aiPoint2DSchema` / `aiPoint3DSchema`
//     from blockSchemas (one truth — no duplicated point shape).
//   - `spaceId` is optional everywhere; the dispatcher resolves it to the
//     active space via `pickSpaceId` (matching INSERT_BLOCK).
//   - TEXT `rotation` is in DEGREES and stored VERBATIM (DXF group code 50 is
//     degrees; the create-text reducer stores it unchanged — unlike INSERT,
//     whose radians model required degrees→radians conversion).

import { z } from "zod";

import { aiPoint2DSchema, aiPoint3DSchema } from "./blockSchemas";

// ═══════════════════════════════════════════════════════════════════════════
// CREATE_TEXT — single-line TEXT entity (DXF TEXT).
// ═══════════════════════════════════════════════════════════════════════════
//
// Maps to `geometry/create-text` (contracts/src/editor.ts). `height` must be
// positive; `rotation` is in DEGREES and passed through unchanged.
export const createTextAiSchema = z.object({
  action: z.literal("CREATE_TEXT"),
  spaceId: z.string().min(1).optional(),
  insertionPoint: aiPoint2DSchema,
  height: z.number().positive(),
  contents: z.string().min(1),
  // DEGREES — stored verbatim on the Text entity (group code 50).
  rotation: z.number().default(0),
  styleName: z.string().min(1).default("Standard"),
});
export type CreateTextAiCommand = z.infer<typeof createTextAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// CREATE_MTEXT — multi-line MTEXT entity (DXF MTEXT).
// ═══════════════════════════════════════════════════════════════════════════
//
// Maps to `geometry/create-mtext`. `nominalHeight` is the text height and
// `referenceWidth` is the column/reference width — both must be positive.
export const createMtextAiSchema = z.object({
  action: z.literal("CREATE_MTEXT"),
  spaceId: z.string().min(1).optional(),
  insertionPoint: aiPoint2DSchema,
  nominalHeight: z.number().positive(),
  referenceWidth: z.number().positive(),
  contents: z.string().min(1),
  styleName: z.string().min(1).default("Standard"),
});
export type CreateMtextAiCommand = z.infer<typeof createMtextAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// CREATE_HATCH — associative HATCH fill (DXF HATCH).
// ═══════════════════════════════════════════════════════════════════════════
//
// Two authoring modes mirror the underlying `geometry/create-hatch` contract
// AND AutoCAD BHATCH:
//   1. Rectangular corner-pick: `boundaryCorner1` + `boundaryCorner2`.
//   2. Internal-point pick: `boundaryPaths` (pre-assembled loops of line/arc
//      edges) + optional `seedPoints` for round-trip fidelity.
// At least one mode MUST be supplied; the SCHEMA (a `.refine`, below) rejects a
// hatch with neither fail-closed at the parse boundary (a HATCH with no boundary
// is meaningless), so the safe contour raises `AiCommandValidationError` and the
// published manifest `inputSchema` advertises the precondition. The translator
// retains an equivalent guard as defense-in-depth. `boundaryPaths` is the full
// edge-loop structure of the HATCH entity — not a simplified stub.

// A single boundary edge — line or arc — mirroring the contract edge union.
export const aiHatchEdgeSchema = z.discriminatedUnion("edgeType", [
  z.object({
    edgeType: z.literal("line"),
    start: aiPoint2DSchema,
    end: aiPoint2DSchema,
  }),
  z.object({
    edgeType: z.literal("arc"),
    center: aiPoint2DSchema,
    radius: z.number().positive(),
    startAngle: z.number(),
    endAngle: z.number(),
    counterclockwise: z.boolean().default(false),
  }),
]);
export type AiHatchEdge = z.infer<typeof aiHatchEdgeSchema>;

// A boundary loop — `pathTypeFlag` mirrors the HATCH 92 path-type flag (1 =
// external, default). Each loop carries ≥1 edge.
export const aiHatchBoundaryPathSchema = z.object({
  pathTypeFlag: z.number().int().default(1),
  edges: z.array(aiHatchEdgeSchema).min(1),
});
export type AiHatchBoundaryPath = z.infer<typeof aiHatchBoundaryPathSchema>;

export const createHatchAiSchema = z
  .object({
    action: z.literal("CREATE_HATCH"),
    spaceId: z.string().min(1).optional(),
    // ── Rectangular corner-pick mode ──
    boundaryCorner1: aiPoint2DSchema.optional(),
    boundaryCorner2: aiPoint2DSchema.optional(),
    // ── Internal-point pick mode (full edge-loop structure) ──
    boundaryPaths: z.array(aiHatchBoundaryPathSchema).optional(),
    seedPoints: z.array(aiPoint2DSchema).optional(),
    patternName: z.string().min(1).default("ANSI31"),
    patternScale: z.number().positive().default(1),
    patternAngle: z.number().default(0),
  })
  // A HATCH with no boundary is meaningless. Exactly one authoring mode must be
  // complete: rectangular corner-pick needs BOTH `boundaryCorner1` and
  // `boundaryCorner2`, internal-point pick needs a non-empty `boundaryPaths`.
  // Encoded at the SCHEMA boundary so the safe contour rejects a no-boundary
  // proposal fail-closed as `AiCommandValidationError`, NOT as the generic,
  // unclassified `Error` the translator would otherwise raise. The translator
  // keeps its own guard as defense-in-depth for hand-built EditorCommands that
  // bypass this parse.
  //
  // NOTE: `z.toJSONSchema` SILENTLY DROPS a `.refine` predicate, so this
  // cross-field precondition is NOT expressible in the emitted JSON Schema. The
  // manifest projects it explicitly via `MANIFEST_SCHEMA_CONSTRAINTS.CREATE_HATCH`
  // (an `anyOf` of the two boundary modes) in `manifest/buildManifest.ts` — that
  // override MIRRORS this refine and must change in lock-step (the anti-drift
  // test enforces a constrained variant carries its `anyOf`).
  .refine(
    (cmd) => {
      const hasCorners =
        cmd.boundaryCorner1 !== undefined && cmd.boundaryCorner2 !== undefined;
      const hasPaths =
        cmd.boundaryPaths !== undefined && cmd.boundaryPaths.length > 0;
      return hasCorners || hasPaths;
    },
    {
      message:
        "CREATE_HATCH requires either boundaryCorner1 + boundaryCorner2 (rectangular mode) or a non-empty boundaryPaths (internal-pick mode); neither was provided.",
      path: ["boundaryCorner1"],
    },
  );
export type CreateHatchAiCommand = z.infer<typeof createHatchAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// CREATE_DIMENSION — the whole DIMENSION family via `dimensionKind`.
// ═══════════════════════════════════════════════════════════════════════════
//
// Only the generic DIMENSION descriptor is `aiCallable: true` (the specific
// DIMALIGNED/DIMANGULAR/DIMRADIUS/… descriptors are aiCallable:false — "not in
// ТЗ §10.1, only generic DIM"). The single `geometry/create-dimension` command
// carries a `dimensionType` (DXF code 70) that selects the variant, so this one
// AI schema covers linear / aligned / angular / diameter / radius / ordinate /
// 3-point-angular by mapping the friendly `dimensionKind` enum → the numeric
// type the contract + reducer expect.
//
// `thirdPoint` is the explicit angular-3-point vertex (DXF code 15/25/35) —
// only meaningful for `dimensionKind: "angular3point"`.

// Friendly enum → numeric dimensionType (group code 70) used by the reducer:
//   linear=0 aligned=1 angular=2 diameter=3 radius=4 angular3point=5 ordinate=6
export const aiDimensionKindSchema = z.enum([
  "linear",
  "aligned",
  "angular",
  "diameter",
  "radius",
  "angular3point",
  "ordinate",
]);
export type AiDimensionKind = z.infer<typeof aiDimensionKindSchema>;

export const AI_DIMENSION_KIND_TO_TYPE: Record<AiDimensionKind, number> = {
  linear: 0,
  aligned: 1,
  angular: 2,
  diameter: 3,
  radius: 4,
  angular3point: 5,
  ordinate: 6,
};

export const createDimensionAiSchema = z
  .object({
    action: z.literal("CREATE_DIMENSION"),
    spaceId: z.string().min(1).optional(),
    extLine1Point: aiPoint2DSchema,
    extLine2Point: aiPoint2DSchema,
    dimensionLinePoint: aiPoint2DSchema,
    // Explicit vertex for 3-point angular dimensions only (DXF 15/25/35).
    thirdPoint: aiPoint3DSchema.optional(),
    dimensionKind: aiDimensionKindSchema.default("linear"),
    // Empty string defers to the measured value (matches contract default).
    dimensionText: z.string().default(""),
  })
  // A 3-point angular dimension is defined by its VERTEX (`thirdPoint`, DXF
  // 15/25/35) plus the two extension-line endpoints. Without the vertex the
  // reducer falls back to `dimensionLinePoint` for the angle origin
  // (geometryReducer.ts:785-789), producing a WRONG angle silently. Require it
  // at the schema boundary so a malformed angular3point proposal is rejected
  // fail-closed before any EditorCommand is produced.
  .refine(
    (cmd) =>
      cmd.dimensionKind !== "angular3point" || cmd.thirdPoint !== undefined,
    {
      message:
        "angular3point dimension requires `thirdPoint` (the angle vertex, DXF 15/25/35)",
      path: ["thirdPoint"],
    },
  );
export type CreateDimensionAiCommand = z.infer<typeof createDimensionAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// CREATE_MLEADER — multileader (DXF MULTILEADER) with one leader + text.
// ═══════════════════════════════════════════════════════════════════════════
//
// Maps to `geometry/create-multileader`. `arrowPoint` is the leader's arrow
// tip, `landingPoint` is the landing/dogleg end where the text attaches, and
// `textContents` is the callout text (non-empty).
export const createMultileaderAiSchema = z.object({
  action: z.literal("CREATE_MLEADER"),
  spaceId: z.string().min(1).optional(),
  arrowPoint: aiPoint2DSchema,
  landingPoint: aiPoint2DSchema,
  textContents: z.string().min(1),
});
export type CreateMultileaderAiCommand = z.infer<
  typeof createMultileaderAiSchema
>;

// ═══════════════════════════════════════════════════════════════════════════
// CREATE_REVCLOUD — revision cloud (REVCLOUD-MODES: all creation methods).
// ═══════════════════════════════════════════════════════════════════════════
//
// Maps to `geometry/create-revcloud`. `arcChordLength` controls the arc-bump
// size (positive; default 1 — matches the contract / REVCLOUD sysvar default).
// `creationMode` mirrors the AutoCAD REVCLOUD methods:
//   Rectangular (default) — corner1 + corner2;
//   Polygonal / Freehand  — vertices[>=3] (Freehand persists as Polygonal);
//   Object                — sourceEntityId of a CLOSED circle / ellipse /
//                           polyline / spline; the source entity is REPLACED
//                           and reverseDirection answers AutoCAD's
//                           "Reverse direction [Yes/No]" prompt.
// `revisionCloudStyle` is the REVCLOUD Style option (Normal | Calligraphy).
export const createRevcloudAiSchema = z
  .object({
    action: z.literal("CREATE_REVCLOUD"),
    spaceId: z.string().min(1).optional(),
    creationMode: z
      .enum(["Rectangular", "Polygonal", "Freehand", "Object"])
      .default("Rectangular"),
    corner1: aiPoint2DSchema.optional(),
    corner2: aiPoint2DSchema.optional(),
    vertices: z.array(aiPoint2DSchema).min(3).optional(),
    sourceEntityId: z.string().min(1).optional(),
    reverseDirection: z.boolean().default(false),
    revisionCloudStyle: z.enum(["Normal", "Calligraphy"]).default("Normal"),
    arcChordLength: z.number().positive().default(1),
  })
  .refine(
    (cmd) => {
      switch (cmd.creationMode) {
        case "Rectangular":
          return cmd.corner1 !== undefined && cmd.corner2 !== undefined;
        case "Polygonal":
        case "Freehand":
          return cmd.vertices !== undefined;
        case "Object":
          return cmd.sourceEntityId !== undefined;
      }
    },
    {
      message:
        "CREATE_REVCLOUD payload incomplete for its creationMode: Rectangular needs corner1+corner2, Polygonal/Freehand need vertices[>=3], Object needs sourceEntityId",
    },
  );
export type CreateRevcloudAiCommand = z.infer<typeof createRevcloudAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Top-level entity-creation AI command discriminated union
// ═══════════════════════════════════════════════════════════════════════════
export const entityAiCommandSchema = z.discriminatedUnion("action", [
  createTextAiSchema,
  createMtextAiSchema,
  createHatchAiSchema,
  createDimensionAiSchema,
  createMultileaderAiSchema,
  createRevcloudAiSchema,
]);
export type EntityAiCommand = z.infer<typeof entityAiCommandSchema>;

// A batch of entity-creation commands the AI can emit in one response.
export const entityAiCommandBatchSchema = z.array(entityAiCommandSchema);
export type EntityAiCommandBatch = z.infer<typeof entityAiCommandBatchSchema>;

// Convenience: literal action names for routing / error messages / telemetry.
export const ENTITY_AI_ACTIONS = [
  "CREATE_TEXT",
  "CREATE_MTEXT",
  "CREATE_HATCH",
  "CREATE_DIMENSION",
  "CREATE_MLEADER",
  "CREATE_REVCLOUD",
] as const;
export type EntityAiAction = (typeof ENTITY_AI_ACTIONS)[number];
